// Row renderers for the top-nav search dropdown: section headers plus one
// branch per row shape (recent search, quick action, recent item, help, nav,
// external, see-all footer, and the library documents). Every handler arrives as
// an argument, so nothing here touches component state.

import React from 'react'
import Icon from '../misc/Icons'
import QuestionCountBadge from '../uiKit/QuestionCountBadge'
import { getDocumentType } from '../../utils/search/getDocumentType'
import { getQuestionBodyLabel } from '../../utils/getQuestionBodyLabel'
import { getRepoforId } from '../../utils/getRepoforId'
import { isCurrentUser } from '../../utils/isCurrentUser'
import { getSectionLabel } from './topNavSearchHelpers'

export function renderSectionTitle(section) {
  if (!section.index) return null
  // `showHeader` is pre-computed in buildAllSections (see topNavSearchHelpers)
  // so renderSectionTitle never has to carry mutable state between calls.
  if (!section.showHeader) return null
  const label = getSectionLabel(section.index)
  // No leading icon on category-header rows — the grey background + dark label
  // (see SearchDropdown.scss) is the visual distinction from result rows.
  return (
    <div className='searchDropdown-sectionHeader'>
      <span className='searchDropdown-sectionHeader-label'>{label}</span>
    </div>
  )
}

// Mouse handlers for previewable rows (set/question/recent set/question). Like
// the recent-search rows, these stop the click from reaching react-autosuggest
// (which would select + collapse the dropdown), so single vs double click can
// be told apart by the parent: single click opens the preview modal, double
// click opens the item.
function previewClickHandlers(suggestion, onClick, onDoubleClick) {
  return {
    onMouseDown: (e) => { e.preventDefault(); e.stopPropagation() },
    onClick: (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (onClick) onClick(suggestion)
    },
    onDoubleClick: (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (onDoubleClick) onDoubleClick(suggestion)
    },
  }
}

// Render a recent-list hit. Sets get the badge icon with count; questions use
// the search-question icon. The resolved item is on suggestion.item.
function renderRecentSuggestion(suggestion, { onPreviewItemClick, onPreviewItemDoubleClick } = {}) {
  const { item, __recentItemType: itemType } = suggestion
  if (!item) return null
  const isSet = itemType === 'set'
  const label = isSet ? item.name : getQuestionBodyLabel(item)
  const handlers = previewClickHandlers(suggestion, onPreviewItemClick, onPreviewItemDoubleClick)
  return (
    <div
      className={`searchDropdownItem searchDropdownItem--recent searchDropdownItem--${isSet ? 'set' : 'question'}`}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...handlers}
    >
      <div className='searchDropdownItem-leadingIcon'>
        {isSet ? (
          <QuestionCountBadge
            color='blueLight'
            size='medium'
            questionCount={Array.isArray(item.questions) ? item.questions.length : 0}
          />
        ) : (
          <QuestionCountBadge color='blueLight' size='medium' questionCount='–' />
        )}
      </div>
      <div className='searchDropdownItem-title'>
        {label}
      </div>
    </div>
  )
}

export const renderSuggestion = (suggestion, {
  onDeleteRecentSearch, onRecentSearchClick, onPreviewItemClick, onPreviewItemDoubleClick,
  onQuickActionClick, onQuickActionMouseDown,
} = {}) => {
  let inSharedRepo = false
  let inPublicConsumerRepo = false
  let inPublicAuthorRepo = false
  if (suggestion.repo) {
    const containingRepo = getRepoforId(suggestion.repo)
    if (containingRepo) {
      inSharedRepo = containingRepo.shared
      inPublicConsumerRepo = containingRepo.role === 'consumer'
      inPublicAuthorRepo = containingRepo.role === 'author'
    }
  }
  if (suggestion.createdBy) {
    if (suggestion.published) {
      if (isCurrentUser(suggestion.author)) {
        inPublicAuthorRepo = true
      } else {
        inPublicConsumerRepo = true
      }
    }
  }

  // Recent search — a previously-run content query. Selecting it re-fills the
  // input and runs the search again (handled in SearchAutocomplete).
  if (suggestion.__recentSearch) {
    return (
      <div
        className='searchDropdownItem searchDropdownItem--recentSearch'
        // Keep the input focused and stop react-autosuggest from selecting +
        // closing the dropdown on click; we handle the fill ourselves so the
        // search runs and the dropdown stays open.
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation() }}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (onRecentSearchClick) onRecentSearchClick(e, suggestion.query)
        }}
      >
        <div className='searchDropdownItem-leadingIcon'>
          <Icon name='magnifying-glass' />
        </div>
        <div className='searchDropdownItem-title'>
          {suggestion.query}
        </div>
        <button
          type='button'
          className='searchDropdownItem-removeBtn'
          aria-label='Remove recent search'
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation() }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (onDeleteRecentSearch) onDeleteRecentSearch(suggestion.query)
          }}
        >
          <Icon name='close-small' />
        </button>
      </div>
    )
  }

  // Quick Action — single-row create affordance. preventDefault on mousedown
  // keeps the input from blurring before the click selects, so a modal-opening
  // quick action can preserve the dropdown query (see openHighlightedSuggestion).
  if (suggestion.__quickAction) {
    return (
      <div
        className='searchDropdownItem searchDropdownItem--quickAction'
        // Keep the input focused and stop react-autosuggest from selecting +
        // collapsing the dropdown on click. react-autosuggest tears the
        // suggestions container down whenever it handles a selection, so a
        // modal-opening quick action (New Pack / New Class) would lose the
        // dropdown that should sit behind the modal. We run the action via our
        // own click handler instead (mirrors the recent-search row).
        //
        // Marking the pending modal on mousedown (earlier than the click) is
        // what keeps the dropdown alive: react-autosuggest has
        // focusInputOnSuggestionClick={false}, so a blur fires around the click
        // and would wipe the query in onBlur before openHighlightedSuggestion
        // sets the flag. The keyboard path has no such blur (Enter keeps focus),
        // which is why it already worked.
        onMouseDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (onQuickActionMouseDown) onQuickActionMouseDown(suggestion)
        }}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (onQuickActionClick) onQuickActionClick(suggestion)
        }}
      >
        <div className='searchDropdownItem-leadingIcon'>
          <Icon name={suggestion.icon || 'plus'} />
        </div>
        <div className='searchDropdownItem-title'>
          {suggestion.label}
        </div>
      </div>
    )
  }

  // Recent item — surfaces last-touched sets/questions
  if (suggestion.__recent) {
    return renderRecentSuggestion(suggestion, { onPreviewItemClick, onPreviewItemDoubleClick })
  }

  // Help "See all results for …" deep-link to help.plickers.com
  if (suggestion.__helpShowMore) {
    return (
      <div className='searchDropdownItem searchDropdownItem--showMore searchDropdownItem--helpShowMore'>
        <div className='searchDropdownItem-title'>
          {'Search Help Center'}
          {suggestion.query && (
            <React.Fragment>
              {' for '}
              <strong>{suggestion.query}</strong>
            </React.Fragment>
          )}
        </div>
      </div>
    )
  }

  // Navigation entry — use class color dot for class entries, compass for others
  if (suggestion.__nav) {
    const isClassEntry = suggestion.color != null
    return (
      <div className='searchDropdownItem searchDropdownItem--nav'>
        <div className='searchDropdownItem-leadingIcon'>
          {isClassEntry ? (
            <div className={`sectionMarker is--colorOnly${suggestion.color ? ` has--classcolor-${suggestion.color}` : ' no--color'} searchDropdownItem-classDot`} />
          ) : (
            <Icon name='compass' />
          )}
        </div>
        <div className='searchDropdownItem-title'>
          {suggestion.label}
        </div>
      </div>
    )
  }

  // External / help link — web icon leading, open-link trailing
  if (suggestion.__external) {
    return (
      <div className='searchDropdownItem searchDropdownItem--external'>
        <div className='searchDropdownItem-leadingIcon'>
          {/* Help-center articles carry their own icon (e.g. info-circle);
              plain external links fall back to the globe. */}
          <Icon name={suggestion.icon && suggestion.icon !== 'external-link' ? suggestion.icon : 'web'} />
        </div>
        <div className='searchDropdownItem-title'>
          {suggestion.label}
        </div>
        <div className='searchDropdownItem-trailingIcon'>
          <Icon name='open-link' />
        </div>
      </div>
    )
  }

  // "See all results for …" footer row
  if (suggestion.showMore) {
    return (
      <div className='searchDropdownItem searchDropdownItem--showMore'>
        <div className='searchDropdownItem-title'>
          See all content results for
          {' '}
          <strong>{suggestion.showMore}</strong>
        </div>
      </div>
    )
  }

  // Library document. Wrapper rows are all handled above, so anything reaching
  // here is a real content hit and getDocumentType discriminates it by shape.
  switch (getDocumentType(suggestion)) {
    case 'repo':
      return (
        <div className='searchDropdownItem searchDropdownItem--repo'>
          <div className='searchDropdownItem-leadingIcon'>
            <Icon name='search-pack' />
          </div>
          <div className='searchDropdownItem-title'>
            {suggestion.name}
            {suggestion.shared && <Icon name='shared' />}
            {inPublicConsumerRepo && <Icon name='globe-shared' />}
            {inPublicAuthorRepo && <Icon name='globe-shared-author' />}
          </div>
        </div>
      )

    case 'folder':
      return (
        <div className='searchDropdownItem searchDropdownItem--folder'>
          <div className='searchDropdownItem-leadingIcon'>
            <Icon name={!suggestion.readOnly ? 'folder' : 'folder-isbe'} />
          </div>
          <div className='searchDropdownItem-title'>
            {suggestion.name}
            {inSharedRepo && <Icon name='shared' />}
            {inPublicConsumerRepo && <Icon name='globe-shared' />}
          </div>
        </div>
      )

    case 'set':
      return (
        <div
          className='searchDropdownItem searchDropdownItem--set'
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...previewClickHandlers(suggestion, onPreviewItemClick, onPreviewItemDoubleClick)}
        >
          <div className='searchDropdownItem-leadingIcon'>
            <QuestionCountBadge
              color='blueLight'
              size='medium'
              questionCount={Array.isArray(suggestion.questions) ? suggestion.questions.length : 0}
            />
          </div>
          <div className='searchDropdownItem-title'>
            {suggestion.name}
            {inSharedRepo && <Icon name='shared' />}
            {inPublicConsumerRepo && <Icon name='globe-shared' />}
          </div>
        </div>
      )

    case 'question':
      return (
        <div
          className='searchDropdownItem searchDropdownItem--question'
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...previewClickHandlers(suggestion, onPreviewItemClick, onPreviewItemDoubleClick)}
        >
          <div className='searchDropdownItem-leadingIcon'>
            <QuestionCountBadge color='blueLight' size='medium' questionCount='–' />
          </div>
          <div className='searchDropdownItem-title'>
            {getQuestionBodyLabel(suggestion)}
            {inSharedRepo && <Icon name='shared' />}
            {inPublicConsumerRepo && <Icon name='globe-shared' />}
          </div>
        </div>
      )

    default:
      return null
  }
}
